import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';

import MasterLocationTableSection from '../../components/MasterLocationTableSection';
import TopLocationSubmittersSection from '../../components/TopLocationSubmittersSection';
import TopRepeatSubmittersSection from '../../components/TopRepeatSubmittersSection';

import type {
  SubmittersLocationData,
  SubmittersMasterLocationData,
  SubmittersRepeatSubmitterData,
  SubmittersRoleData,
  TicketingSubmittersLocationsPayload,
} from '../../components/types';
import VerticalBarChartCard from '../../components/VerticalBarChartCard';

interface SubmittersLocationsProps {
  loading: boolean;
  error: string | null;
  data: TicketingSubmittersLocationsPayload | null;
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const SubmittersLocations: React.FC<SubmittersLocationsProps> = ({
  loading,
  error,
  data,
  onRetry,
  hasActiveFilters = false,
}) => {
  const roles = useMemo<SubmittersRoleData[]>(() => {
    return (data?.ticketsByRole?.roles ?? []).map((item) => ({
      name: item.name,
      count: item.count,
      percentage: item.percentage,
    }));
  }, [data?.ticketsByRole?.roles]);

  const topRepeatRows = useMemo<SubmittersRepeatSubmitterData[]>(() => {
    return (data?.topRepeatSubmitters?.submitters ?? []).map((item) => ({
      user: item.user,
      ticketsSubmitted: item.ticketsSubmitted,
      ticketsClosed: item.ticketsClosed,
      ticketsOpen: item.ticketsOpen,
      resolutionRate: item.resolutionRate,
      averageResolutionTime: item.averageResolutionTime,
      topCategoryName: item.topCategoryName,
      topCategoryCount: item.topCategoryCount,
      navigatorRole: item.navigatorRole,
      location: item.location,
    }));
  }, [data?.topRepeatSubmitters?.submitters]);

  const topLocationRows = useMemo<SubmittersLocationData[]>(() => {
    return (data?.topLocationSubmitters?.locations ?? []).map((item) => ({
      location: item.location,
      ticketsSubmitted: item.ticketsSubmitted,
      ticketsClosed: item.ticketsClosed,
      ticketsOpen: item.ticketsOpen,
      resolutionRate: item.resolutionRate,
      averageResolutionTime: item.averageResolutionTime,
      topCategoryName: item.topCategoryName,
      topCategoryCount: item.topCategoryCount,
    }));
  }, [data?.topLocationSubmitters?.locations]);

  const masterRows = useMemo<SubmittersMasterLocationData[]>(() => {
    return (data?.masterLocationTable?.locations ?? []).map((item) => ({
      location: item.location,
      sapId: item.sapId,
      ticketsSubmitted: item.ticketsSubmitted,
      ticketsClosed: item.ticketsClosed,
      ticketsOpen: item.ticketsOpen,
      resolutionRate: item.resolutionRate,
      averageResolutionTime: item.averageResolutionTime,
      topCategoryName: item.topCategoryName,
      topCategoryCount: item.topCategoryCount,
    }));
  }, [data?.masterLocationTable?.locations]);

  return (
    <View style={styles.content}>
    <VerticalBarChartCard
      title={
        data?.ticketsByRole?.title ??
        'Tickets by Role'
      }
      subtitle={
        data?.ticketsByRole?.description ??
        ''
      }
      data={roles.map(item => ({
        label: item.name,
        value: item.count,
        valueLabel:
          item.count.toLocaleString(),
        percentage: item.percentage,
      }))}
      metadataPosition="inside"
      loading={loading}
      skeletonBarCount={5}
      error={error}
      onRetry={onRetry}
      hasActiveFilters={hasActiveFilters}
    />

      <TopRepeatSubmittersSection
        title={data?.topRepeatSubmitters?.title ?? 'Top Repeat Submitters'}
        description={data?.topRepeatSubmitters?.description ?? ''}
        rows={topRepeatRows}
        loading={loading}
        skeletonRowCount={5}
        error={error}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
      />

      <TopLocationSubmittersSection
        title={data?.topLocationSubmitters?.title ?? 'Top Location Submitters'}
        description={data?.topLocationSubmitters?.description ?? ''}
        rows={topLocationRows}
        loading={loading}
        skeletonRowCount={5}
        error={error}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
      />

      <MasterLocationTableSection
        title={data?.masterLocationTable?.title ?? 'Master Location Table'}
        searchable={data?.masterLocationTable?.searchable ?? true}
        searchPlaceholder={
          data?.masterLocationTable?.searchPlaceholder ??
          'Search locations'
        }
        rows={masterRows}
        loading={loading}
        skeletonRowCount={8}
        error={error}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 2,
  },
});

export default SubmittersLocations;